import React from 'react';
import { Banknote, Clock, Receipt } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './Card';
import { Badge } from './Badge';
import { StatTile } from './StatTile';
import type { Expert } from '../types/expert';
import { engagementCount, formatAED, formatDate, pendingAmount, totalPaid } from '../utils/expert';
type PaymentsPanelProps = {
  expert: Expert;
};
export function PaymentsPanel({
  expert
}: PaymentsPanelProps) {
  const paid = totalPaid(expert);
  const pending = pendingAmount(expert);
  const completed = engagementCount(expert);
  const avg = completed > 0 ? Math.round((paid + pending) / completed) : 0;
  const payments = expert.events.filter(e => e.status !== 'Upcoming').sort((a, b) => b.date.localeCompare(a.date));
  const byYear = payments.reduce<Record<string, number>>((acc, e) => {
    if (e.status !== 'Paid') return acc;
    const y = e.date.slice(0, 4);
    acc[y] = (acc[y] ?? 0) + e.fee;
    return acc;
  }, {});
  const years = Object.entries(byYear).sort(([a], [b]) => b.localeCompare(a));
  const maxYear = Math.max(...years.map(([, v]) => v), 1);
  return <div className="flex flex-col gap-4" data-id="element-507" data-mp="28znunt5shmut">
      <div className="grid gap-3 sm:grid-cols-3" data-id="element-508" data-mp="2mxchh6jf342i">
        <StatTile label="Total paid" value={formatAED(paid)} hint={`${payments.filter(p => p.status === 'Paid').length} settled invoices`} icon={Banknote} data-id="element-509" data-mp="2muykgs56fhsb" />
        <StatTile label="Pending payment" value={formatAED(pending)} hint="Awaiting finance approval" icon={Clock} data-id="element-510" data-mp="1v6y73kam49vl" />
        <StatTile label="Avg. per engagement" value={formatAED(avg)} hint={`Across ${completed} engagements`} icon={Receipt} data-id="element-511" data-mp="1q8d81o73jw8q" />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_280px]" data-id="element-512" data-mp="1z7i5ssau3lju">
        <Card data-id="element-513" data-mp="1z5xbn3sfm4mi">
          <CardHeader data-id="element-514" data-mp="1z5dptbznuw37">
            <CardTitle data-id="element-515" data-mp="1bhw33uv96o73">Payment history</CardTitle>
            <CardDescription data-id="element-516" data-mp="1bgl03x9mtkh1">Invoices raised for completed engagements.</CardDescription>
          </CardHeader>
          <CardContent data-id="element-517" data-mp="17g27i0zx0ads">
            <ul className="divide-y" data-id="element-518" data-mp="17ehmp33ek5t4">
              {payments.map((p, i) => <li key={p.id} className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0" data-id="element-519" data-mp="36kslkkgdgvh4" data-mpi={i}>
                  <div className="min-w-0" data-id="element-520" data-mp="3bhspoptnvo5l">
                    <p className="truncate text-sm font-medium" data-id="element-521" data-mp="3galufdp4qwho">{p.name}</p>
                    <p className="text-xs text-muted-foreground" data-id="element-522" data-mp="3l9oz2x1770p4">
                      INV-{expert.id.slice(0, 3).toUpperCase()}-{String(1040 + i)} · {formatDate(p.date)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-523" data-mp="2skhsdarlb4ss">
                    <span className="text-sm font-semibold tabular-nums" data-id="element-524" data-mp="2sn47pwkq2olk">{formatAED(p.fee)}</span>
                    <Badge variant="outline" className={p.status === 'Paid' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'} data-id="element-525" data-mp="2xmqlc74gq6uw">
                      {p.status}
                    </Badge>
                  </div>
                </li>)}
            </ul>
          </CardContent>
        </Card>

        <Card data-id="element-526" data-mp="1xcfuu78pe2jo">
          <CardHeader data-id="element-527" data-mp="1xbxnofki63wz">
            <CardTitle data-id="element-528" data-mp="1xdii856thmuu">Paid by year</CardTitle>
          </CardHeader>
          <CardContent data-id="element-529" data-mp="1skxpl6zndfsw">
            {years.length === 0 ? <p className="text-sm text-muted-foreground" data-id="element-530" data-mp="1sj4k3v0rt1mh">No settled payments yet.</p> : <ul className="space-y-4" data-id="element-531" data-mp="1nkjs2ivo9o94">
                {years.map(([year, value]) => <li key={year} data-id="element-532" data-mp="1ikyizfmsfzio">
                    <div className="flex justify-between text-xs" data-id="element-533" data-mp="1el6ilq7jszoe">
                      <span className="font-medium" data-id="element-534" data-mp="1eo2frcbknkbs">{year}</span>
                      <span className="tabular-nums text-muted-foreground" data-id="element-535" data-mp="19n50p8luz6ei">{formatAED(value)}</span>
                    </div>
                    <div className="mt-1.5 h-2 rounded-full bg-muted" data-id="element-536" data-mp="14pms5jbr6t71">
                      <div className="h-2 rounded-full bg-primary" style={{
                  width: `${value / maxYear * 100}%`
                }} data-id="element-537" data-mp="3404agl3k56dv" />
                    </div>
                  </li>)}
              </ul>}
          </CardContent>
        </Card>
      </div>
    </div>;
}